/**
 * Inline SVG icons for the "Recent Activity" tab strip and the activity feed.
 *
 * The legacy pages used Font Awesome glyphs on each tab (`<i class="fa fa-…">`)
 * and a per-event PNG in the activity list. Fonts are not loaded here, so the
 * same shapes are drawn inline — no network dependency, and they inherit the
 * current text colour so the active/inactive tab states just work.
 */

export type TabIconName =
  | 'activities'
  | 'calls'
  | 'followup'
  | 'notes'
  | 'documents'
  | 'task'
  | 'appointment'
  | 'chat'
  | 'webform'
  | 'pipeline'
  | 'wachat'
  | 'interaction'
  | 'email'
  | 'sms'
  | 'voice'
  | 'deal'
  | 'document'
  | 'lead'
  | 'score'
  | 'activity'

const S = {
  width: 14,
  height: 14,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  focusable: false,
}

/** One icon, sized for a tab label or an activity row. */
export function TabIcon({ name, size = 14 }: { name: TabIconName; size?: number }) {
  const props = { ...S, width: size, height: size }

  switch (name) {
    case 'activities':
    case 'activity':
      return (
        <svg {...props}>
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
        </svg>
      )
    case 'calls':
    case 'voice':
      return (
        <svg {...props}>
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
      )
    case 'followup':
    case 'interaction':
      return (
        <svg {...props}>
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
          <polyline points="9 16 11 18 15 14" />
        </svg>
      )
    case 'notes':
      return (
        <svg {...props}>
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
        </svg>
      )
    case 'documents':
    case 'document':
      return (
        <svg {...props}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
        </svg>
      )
    case 'task':
      return (
        <svg {...props}>
          <line x1="10" y1="6" x2="21" y2="6" />
          <line x1="10" y1="12" x2="21" y2="12" />
          <line x1="10" y1="18" x2="21" y2="18" />
          <polyline points="3 6 4 7 7 4" />
          <polyline points="3 12 4 13 7 10" />
          <polyline points="3 18 4 19 7 16" />
        </svg>
      )
    case 'appointment':
      return (
        <svg {...props}>
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      )
    case 'chat':
    case 'sms':
      return (
        <svg {...props}>
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      )
    case 'webform':
      return (
        <svg {...props}>
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <line x1="7" y1="8" x2="17" y2="8" />
          <line x1="7" y1="12" x2="17" y2="12" />
          <line x1="7" y1="16" x2="13" y2="16" />
        </svg>
      )
    case 'pipeline':
    case 'deal':
      return (
        <svg {...props}>
          <line x1="18" y1="20" x2="18" y2="10" />
          <line x1="12" y1="20" x2="12" y2="4" />
          <line x1="6" y1="20" x2="6" y2="14" />
        </svg>
      )
    case 'wachat':
      return (
        <svg {...props}>
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
        </svg>
      )
    case 'email':
      return (
        <svg {...props}>
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
          <polyline points="22,6 12,13 2,6" />
        </svg>
      )
    case 'lead':
      return (
        <svg {...props}>
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      )
    case 'score':
      return (
        <svg {...props}>
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      )
    default:
      return null
  }
}

/**
 * Maps an activity row's EventType / EventName to an icon, used when the API's
 * EventIconURL image is missing or fails to load.
 */
export function activityIconName(type: string, title = ''): TabIconName {
  const text = `${type} ${title}`.toLowerCase()
  if (text.includes('whatsapp') || text.includes('wa ')) return 'wachat'
  if (text.includes('chat')) return 'chat'
  if (text.includes('sms')) return 'sms'
  if (text.includes('mail') || text.includes('email')) return 'email'
  if (text.includes('call') || text.includes('voice')) return 'calls'
  if (text.includes('interaction') || text.includes('followup') || text.includes('follow up')) return 'followup'
  if (text.includes('note')) return 'notes'
  if (text.includes('doc')) return 'documents'
  if (text.includes('appointment')) return 'appointment'
  if (text.includes('task')) return 'task'
  if (text.includes('deal') || text.includes('pipeline') || text.includes('stage')) return 'pipeline'
  if (text.includes('webform') || text.includes('form')) return 'webform'
  if (text.includes('score')) return 'score'
  if (text.includes('lead') || text.includes('enquiry')) return 'lead'
  return 'activity'
}
